import { format, isValid, parse } from "date-fns";
import { calculateStudentAgeInMonths } from "@/lib/utils/classRegistrationEligibility";
import {
  getRuntimeTenantTimeZone,
  resolveIanaTimeZone,
} from "./studio-clock";

export function formatTimeTo12Hour(timeString?: string | null): string {
  if (!timeString) return "";
  try {
    // Try parsing with "HH:mm:ss" format first
    const date = parse(timeString, "HH:mm:ss", new Date());
    return format(date, "h:mm a");
  } catch {
    try {
      // If that fails, try "HH:mm" format
      const date = parse(timeString, "HH:mm", new Date());
      return format(date, "h:mm a");
    } catch {
      return timeString ?? "";
    }
  }
}

/** Whole years, or .5 after 6 completed months in that year. */
export const formatAge = (
  dateOfBirth: Date,
  asOf: Date = new Date(),
): string => {
  const months = calculateStudentAgeInMonths(dateOfBirth, asOf);
  const years = Math.floor(months / 12);
  const extraMonths = months % 12;
  const isHalfYear = extraMonths >= 6;
  const label = isHalfYear ? `${years}.5` : String(years);
  const unit = !isHalfYear && years === 1 ? "year" : "years";
  return `${label} ${unit} old`;
};

const FALLBACK_DATE_LABEL = "-";

export function safeParseDate(
  value: Date | string | null | undefined,
): Date | null {
  if (value == null || value === "") return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** date-fns `format` that returns a fallback when the value is missing or invalid. */
export function formatWithDateFnsSafe(
  value: Date | string | null | undefined,
  pattern: string,
  fallback = FALLBACK_DATE_LABEL,
): string {
  const d = safeParseDate(value);
  if (!d || !isValid(d)) return fallback;
  return format(d, pattern);
}

export function formatDateSafe(
  value: Date | string | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
  },
  locale = "en-US",
): string {
  const d = safeParseDate(value);
  if (!d) return FALLBACK_DATE_LABEL;
  return d.toLocaleDateString(locale, options);
}

export function formatDateOnlySafe(
  value: Date | string | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
  },
  locale = "en-US",
): string {
  const d = safeParseDate(value);
  if (!d) return FALLBACK_DATE_LABEL;
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  const day = d.getUTCDate();
  const atNoon = new Date(Date.UTC(y, m, day, 12, 0, 0));
  return atNoon.toLocaleDateString(locale, { ...options, timeZone: "UTC" });
}

/** Format YYYY-MM-DD string to app display (month-day-year), e.g. "Mar 15, 2025". */
export function formatDateTableDisplay(
  dateStr: string | null | undefined,
): string {
  if (!dateStr || typeof dateStr !== "string") return FALLBACK_DATE_LABEL;
  try {
    const d = parse(dateStr, "yyyy-MM-dd", new Date());
    if (Number.isNaN(d.getTime())) return FALLBACK_DATE_LABEL;
    return format(d, "MMM d, yyyy");
  } catch {
    return FALLBACK_DATE_LABEL;
  }
}

function isUtcMidnightInstant(date: Date): boolean {
  return (
    date.getUTCHours() === 0 &&
    date.getUTCMinutes() === 0 &&
    date.getUTCSeconds() === 0 &&
    date.getUTCMilliseconds() === 0
  );
}

/**
 * Splits a timestamp into a calendar date and clock time.
 * Date-only / UTC-midnight values omit time so we do not show a fake 12:00 AM.
 */
export function getDateTimeDisplayParts(
  value: Date | string | null | undefined,
  options?: { compact?: boolean },
): { dateLabel: string; timeLabel: string | null } | null {
  const d = safeParseDate(value);
  if (!d) return null;

  const compact = options?.compact ?? false;

  if (isUtcMidnightInstant(d)) {
    return {
      dateLabel: formatDateOnlySafe(
        d,
        compact
          ? { month: "short", day: "numeric" }
          : { month: "short", day: "numeric", year: "numeric" },
      ),
      timeLabel: null,
    };
  }

  const studioTz = resolveIanaTimeZone(getRuntimeTenantTimeZone());
  if (studioTz) {
    const dateLabel = new Intl.DateTimeFormat("en-US", {
      timeZone: studioTz,
      month: "short",
      day: "numeric",
      ...(compact ? {} : { year: "numeric" }),
    }).format(d);
    const timeLabel = new Intl.DateTimeFormat("en-US", {
      timeZone: studioTz,
      hour: "numeric",
      minute: "2-digit",
    }).format(d);
    return { dateLabel, timeLabel };
  }

  return {
    dateLabel: format(d, compact ? "MMM d" : "MMM d, yyyy"),
    timeLabel: format(d, "h:mm a"),
  };
}

/** Enrollment / event timestamp, e.g. "Sep 14, 2026 · 3:42 PM". */
export function formatDateTimeDisplay(
  value: Date | string | null | undefined,
  options?: { compact?: boolean; fallback?: string },
): string {
  const parts = getDateTimeDisplayParts(value, { compact: options?.compact });
  if (!parts) return options?.fallback ?? FALLBACK_DATE_LABEL;
  if (!parts.timeLabel) return parts.dateLabel;
  return `${parts.dateLabel} · ${parts.timeLabel}`;
}
